import React from "react";
import { Line } from "react-chartjs-2";

import { Flex } from "antd";
// eslint-disable-next-line import/no-duplicates
import "chart.js/auto";
// eslint-disable-next-line import/no-duplicates
import { CartesianTickOptions, ChartDataset, ScriptableLineSegmentContext } from "chart.js/auto";

import { Icon, Tooltip } from "components";

const AXIS_TICK_CONFIG: Partial<CartesianTickOptions> = {
  crossAlign: "far",
  color: "#100c06",
  font: {
    family: "dazzed",
    weight: 500,
    size: 12,
  },
};

/**
 * Colors come in pairs, the line color is 'color-500' and the fill color is
 * 'color-100'. Values taken from `variables.scss`
 */
const COLORS = {
  white: {
    fill: "#fff",
    border: "#fff",
  },
  green: {
    fill: "#ebf2f0",
    border: "#06382b",
  },
  red: {
    fill: "#ffe6df",
    border: "#db3804",
  },
  orange: {
    fill: "#ffecdb",
    border: "#e77307",
  },
  blue: {
    fill: "#e8effe",
    border: "#4d85f5",
  },
};

/** 6px of line followed by 4px of blank space */
const DASH_CONFIG = [4, 4];

export interface LineChartLine {
  label: string;
  color?: keyof typeof COLORS;
  /** Controls whether or not the entire line is dashed or not */
  dashed?: boolean;
  points: Array<{
    label?: string;
    /** Controls whether or not the point is filled in or white in the center */
    filled?: boolean;
    value: number;
    /** Controls whether or not the line segment approaching this point is dashed or not  */
    dashed?: boolean;
    /** Overrides the color of the line segement approaching this point and the point itself */
    color?: keyof typeof COLORS;
    /** Controls the shadow range above/below the point */
    range?: number;
  }>;
}

interface ShadowLine extends LineChartLine {
  isShadow?: boolean;
  isBottom?: boolean;
}

export interface LineChartProps {
  title?: string;
  subtitle?: string;
  tooltip?: string;
  labels: string[];
  lines: LineChartLine[];
  height?: number;
  width?: number;
  /** Numeric range for how much above and below the min/max points */
  paddingY?: number;
}

export const LineChart = ({ title, subtitle, tooltip, labels, lines, height, width, paddingY }: LineChartProps) => {
  // If any point has a range, we duplicate it into another two lines (one above
  // and one below) so we can fill in between them
  const shadowLines = lines
    .filter((l) => l.points.some((p) => p.range))
    .flatMap<ShadowLine>((line) => [
      {
        ...line,
        isShadow: true,
        points: line.points.map((p) => ({ ...p, value: p.value + (p.range ?? 0) })),
      },
      {
        ...line,
        isShadow: true,
        isBottom: true,
        points: line.points.map((p) => ({ ...p, value: p.value - (p.range ?? 0) })),
      },
    ]);

  // Lines are drawn back to front, so put the shadows "behind" the real lines
  const allLines = [...lines, ...shadowLines];
  const [minValue, maxValue] = allLines.reduce(
    (acc, line) => {
      line.points.forEach((p) => {
        if (acc[0] === null || p.value < acc[0]) acc[0] = p.value;
        if (acc[1] === null || p.value > acc[1]) acc[1] = p.value;
      });
      return acc;
    },
    [null, null] as [number | null, number | null]
  );

  if (minValue === null || maxValue === null) {
    // eslint-disable-next-line no-console
    console.warn("LineChartError: Invalid data, unable to determine min/max values");
  }

  // Default padding is 30% of the range of the values (because I tried a bunch
  // of default ranges and I thought this looked good)
  const verticalPadding = paddingY ?? ((maxValue ?? 0) - (minValue ?? 0)) * 0.3;

  return (
    <Flex vertical gap="0.5rem">
      {title ? (
        <Tooltip placement="bottomLeft" disabled={tooltip === undefined} title={tooltip}>
          <Flex gap="1rem" align="center">
            <h6>{title}</h6>
            {tooltip ? <Icon size="s" iconName="tabler/info-circle" /> : null}
          </Flex>
        </Tooltip>
      ) : null}
      {subtitle ? <p className="margin-top-0 paragraph-s grey-600-text">{subtitle}</p> : null}
      <Line
        height={height}
        width={width ?? height}
        data={{
          labels: labels,
          datasets: allLines.map(
            (line: ShadowLine) =>
              ({
                data: line.points.map((p) => p.value),
                pointBorderColor: (ctx) => COLORS[line.points[ctx.dataIndex].color ?? line.color ?? "green"].border,
                pointBackgroundColor: (ctx) => {
                  if (line.points[ctx.dataIndex].filled === true) return COLORS[line.points[ctx.dataIndex].color ?? line.color ?? "green"].border;
                  return COLORS[line.points[ctx.dataIndex].color ?? line.color ?? "white"].fill;
                },
                segment: {
                  borderColor: (lineSegmentCtx: ScriptableLineSegmentContext) =>
                    COLORS[line.points[lineSegmentCtx.p1DataIndex]?.color ?? line.color ?? "green"].border,
                  borderDash: (lineSegmentCtx: ScriptableLineSegmentContext) => {
                    if (line.dashed === true) return DASH_CONFIG;
                    return line.points[lineSegmentCtx.p1DataIndex]?.dashed ? DASH_CONFIG : [];
                  },
                },

                // Shadow lines always come in [top, bottom] pairs, this tells chartjs
                // to fill between the bottom line and the previous line (the top line)
                fill: line.isBottom ? "-1" : false,
                backgroundColor: line.isBottom ? COLORS[line.color ?? "green"].fill : undefined,
                borderWidth: line.isShadow ? 0 : 2,
                pointRadius: line.isShadow ? 0 : 4,
                pointHitRadius: line.isShadow ? 0 : 10,
              }) as ChartDataset<"line">
          ),
        }}
        options={{
          plugins: {
            legend: { display: false },
            datalabels: { display: false },
            // Ensures fill areas are drawn behind grid lines
            filler: { drawTime: "beforeDraw" },
          },
          scales: {
            x: {
              display: true,
              border: { display: false },
              ticks: { ...AXIS_TICK_CONFIG, padding: 16 },
            },
            y: {
              display: true,
              grid: { display: false },
              ticks: AXIS_TICK_CONFIG,
              suggestedMin: minValue ? minValue - verticalPadding : undefined,
              suggestedMax: maxValue ? maxValue + verticalPadding : undefined,
            },
          },
        }}
      />
    </Flex>
  );
};

export default LineChart;
